import { AsyncPipe, DecimalPipe, DatePipe } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { MatSort, MatSortHeader } from '@angular/material/sort';
import {
  MatTableDataSource,
  MatTable,
  MatColumnDef,
  MatHeaderCellDef,
  MatHeaderCell,
  MatCellDef,
  MatCell,
  MatHeaderRowDef,
  MatHeaderRow,
  MatRowDef,
  MatRow,
} from '@angular/material/table';
import {
  DaterangeConfig,
  PageToolbarComponent,
  DaterangePickerComponent,
  NotificationEmptyComponent,
} from '@next/core-lib/components';
import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';
import { SiteActions, SiteSelectors, SiteState } from '@next/core-lib/site';
import { select, Store } from '@ngrx/store';

import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

import { MilkDeliverySummary } from '../../models/milk-delivery.model';
import * as MilkDeliveryActions from '../../store/actions/milk-delivery.actions';
import { getMilkDeliverySummary } from './../../store/selectors/milk-delivery.selectors';

@Component({
  selector: 'app-milk-delivery',
  templateUrl: './milk-delivery.component.html',
  styleUrls: ['./milk-delivery.component.scss'],
  imports: [
    PageToolbarComponent,
    DaterangePickerComponent,
    MatTable,
    MatSort,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatSortHeader,
    MatCellDef,
    MatCell,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    NotificationEmptyComponent,
    AsyncPipe,
    DecimalPipe,
    DatePipe,
    TranslatePipe,
  ],
})
export class MilkDeliveryComponent implements OnInit, OnDestroy {
  private readonly store = inject<Store<SiteState>>(Store);
  private readonly localeService = inject(LocaleService);

  @ViewChild(MatSort) sort: MatSort;
  public pageTitle$ = this.store.pipe(select(SiteSelectors.selectPageTitle));
  public customDateRangeConfig: { presets: Presets[]; initial: DateRange };
  public dataSource = new MatTableDataSource<MilkDeliverySummary>();
  public dateFormat: string;
  public columns: {
    id: string;
    label: string;
    subLabel?: string;
    type: 'date' | 'number';
    format?: string;
  }[] = [
    {
      id: 'date',
      label: 'dairyresults.milk-delivery.milk-delivery-summary.date',
      type: 'date',
    },
    {
      id: 'milkKiloGrams',
      label: 'dairyresults.milk-delivery.milk-delivery-summary.milkKiloGrams',
      type: 'number',
      format: '1.0-0',
      subLabel: 'common.kilogram',
    },
    {
      id: 'fatPercentage',
      label: 'dairyresults.milk-delivery.milk-delivery-summary.fatPercentage',
      type: 'number',
      format: '1.2-2',
      subLabel: 'common.percentage',
    },
    {
      id: 'proteinPercentage',
      label: 'dairyresults.milk-delivery.milk-delivery-summary.proteinPercentage',
      type: 'number',
      format: '1.2-2',
      subLabel: 'common.percentage',
    },
    {
      id: 'ureum',
      label: 'dairyresults.milk-delivery.milk-delivery-summary.ureum',
      type: 'number',
      format: '1.0-0',
    },
    {
      id: 'lactosePercentage',
      label: 'dairyresults.milk-delivery.milk-delivery-summary.lactosePercentage',
      type: 'number',
      format: '1.2-2',
      subLabel: 'common.percentage',
    },
    {
      id: 'somaticCellCount',
      label: 'dairyresults.milk-delivery.milk-delivery-summary.somaticCellCount',
      type: 'number',
      format: '1.0-0',
    },
    {
      id: 'butyricAcid',
      label: 'dairyresults.milk-delivery.milk-delivery-summary.butyricAcid',
      type: 'number',
      format: '1.2-2',
    },
    {
      id: 'phosphorus',
      label: 'dairyresults.milk-delivery.milk-delivery-summary.phosphorus',
      type: 'number',
      format: '1.0-0',
    },
    {
      id: 'palmiticAcid',
      label: 'dairyresults.milk-delivery.milk-delivery-summary.palmiticAcid',
      type: 'number',
      format: '1.1-1',
    },
    {
      id: 'acidity',
      label: 'dairyresults.milk-delivery.milk-delivery-summary.acidity',
      type: 'number',
      format: '1.0-2',
    },
  ];

  private readonly destroy$ = new Subject<void>();

  private getMilkDeliverySummary$ = this.store.select(getMilkDeliverySummary);

  ngOnInit(): void {
    this.store.dispatch(
      SiteActions.updatePageTitle({ pageTitle: 'dairyresults.milk-delivery.title' }),
    );
    this.dateFormat = this.localeService.translate('common.date-format') ?? 'dd-MM-yyyy';

    this.customDateRangeConfig = {
      presets: [...new DaterangeConfig().defaultConfig.presets],
      initial: new DaterangeConfig().defaultConfig.presets[0],
    };

    this.getMilkDeliverySummary$
      .pipe(
        filter((milkDeliverySummary) => !!milkDeliverySummary),
        takeUntil(this.destroy$),
      )
      .subscribe((milkDeliverySummary) => {
        this.dataSource.data = milkDeliverySummary;
        this.dataSource.sort = this.sort;
      });

    this.store.dispatch(
      MilkDeliveryActions.loadMilkDeliveriesRequest({
        start: this.customDateRangeConfig.presets[0].startDate.toISOString(),
        end: this.customDateRangeConfig.presets[0].endDate.toISOString(),
      }),
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
  public columnIds = (): string[] => this.columns.map((item) => item.id);
  public hasData = (): boolean => this.dataSource?.data?.length > 0;

  public onDateRangeChanged(dateRange: DateRange): void {
    this.store.dispatch(
      MilkDeliveryActions.loadMilkDeliveriesRequest({
        start: dateRange.startDate.toISOString(),
        end: dateRange.endDate.toISOString(),
      }),
    );
  }

  public initTable(): boolean {
    if (!this.dataSource.sort) {
      this.dataSource.sort = this.sort;
    }

    return this.hasData();
  }

  public allignRight = (type: string): boolean => type === 'number';
}

interface DateRange {
  description?: string;
  startDate: Date;
  endDate: Date;
}

interface Presets {
  description: string;
  startDate: Date;
  endDate: Date;
}
